import type { ArchiveSnapshotSummary, ArchivedOfficialReference, Official } from '../../apiClient';
import { buildArchiveHistoryTimeline } from '../../officialDetailArchive.ts';
import type { ReceiptMoment } from './share';

export type FreshnessTone = 'fresh' | 'recent' | 'aging' | 'stale' | 'unknown';

export interface FreshnessSummary {
  tone: FreshnessTone;
  label: string;
  detail: string;
  timestamp: number | null;
  ageMs: number | null;
}

export type CaseTimelineEntryKind = ReceiptMoment['kind'] | 'archive_snapshot';

export interface CaseTimelineEntry {
  id: string;
  kind: CaseTimelineEntryKind;
  title: string;
  summary: string;
  occurredAt: number;
  timestampLabel: string;
  badgeLabel: string;
  sourceLabel: string;
  anchorId: string | null;
  href: string | null;
  external: boolean;
  isLatest: boolean;
}

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

function formatTimestamp(timestamp: number): string {
  return new Date(timestamp).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function truncate(value: string, max = 180): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1).trimEnd()}…`;
}

export function describeFreshness(timestamp: number | null | undefined, now = Date.now()): FreshnessSummary {
  if (!Number.isFinite(timestamp)) {
    return {
      tone: 'unknown',
      label: 'Timestamp unavailable',
      detail: 'We do not have a reliable freshness timestamp for this dossier yet.',
      timestamp: null,
      ageMs: null,
    };
  }

  const value = Number(timestamp);
  const ageMs = Math.max(0, now - value);

  if (ageMs < DAY_MS) {
    return {
      tone: 'fresh',
      label: 'Fresh today',
      detail: 'Updated within the last 24 hours.',
      timestamp: value,
      ageMs,
    };
  }

  if (ageMs < 3 * DAY_MS) {
    return {
      tone: 'recent',
      label: 'Recent 72h',
      detail: 'Updated within the last three days.',
      timestamp: value,
      ageMs,
    };
  }

  if (ageMs < WEEK_MS) {
    return {
      tone: 'aging',
      label: 'This week',
      detail: 'Updated within the last seven days.',
      timestamp: value,
      ageMs,
    };
  }

  return {
    tone: 'stale',
    label: 'Needs refresh',
    detail: 'No source-backed movement has landed in over a week.',
    timestamp: value,
    ageMs,
  };
}

export function buildCaseTimeline(input: {
  official: Pick<Official, 'name' | 'updated_at' | 'accountability_summary'>;
  receiptMoments: ReceiptMoment[];
  archiveHistory?: ArchiveSnapshotSummary[];
  replacedByOfficial?: ArchivedOfficialReference | null;
}): CaseTimelineEntry[] {
  const receiptEntries = input.receiptMoments.map((moment): CaseTimelineEntry => ({
    id: `receipt-${moment.id}`,
    kind: moment.kind,
    title: moment.claim,
    summary: moment.summary,
    occurredAt: moment.occurredAt,
    timestampLabel: formatTimestamp(moment.occurredAt),
    badgeLabel: moment.impactLabel,
    sourceLabel: moment.kind === 'accountability_record'
      ? 'Accountability record'
      : moment.kind === 'activity'
        ? 'Public activity'
        : 'Dossier refresh',
    anchorId: moment.anchorId,
    href: moment.anchorId ? `#${moment.anchorId}` : null,
    external: false,
    isLatest: false,
  }));

  const archiveTimeline = buildArchiveHistoryTimeline(input.archiveHistory ?? [], input.replacedByOfficial ?? null);
  const archiveEntries = archiveTimeline.map((item): CaseTimelineEntry => ({
    id: `archive-${item.id}`,
    kind: 'archive_snapshot',
    title: item.reasonLabel,
    summary: truncate(item.successorName ? `Successor on record: ${item.successorName}.` : 'Archived lifecycle snapshot preserved for this officeholder.'),
    occurredAt: item.occurredAt,
    timestampLabel: formatTimestamp(item.occurredAt),
    badgeLabel: item.isLatest ? 'latest archive' : 'archive snapshot',
    sourceLabel: 'Lifecycle archive',
    anchorId: null,
    href: item.successorId ? `/officials/${item.successorId}` : null,
    external: false,
    isLatest: false,
  }));

  const latestReceiptAt = receiptEntries.reduce((max, item) => Math.max(max, item.occurredAt), 0);
  if (input.official.updated_at > latestReceiptAt) {
    receiptEntries.push({
      id: 'receipt-dossier-refresh-current',
      kind: 'dossier_refresh',
      title: `${input.official.name}'s dossier was refreshed`,
      summary: truncate(input.official.accountability_summary ?? 'The dossier refreshed after the latest evidence move to keep the case file current.'),
      occurredAt: input.official.updated_at,
      timestampLabel: formatTimestamp(input.official.updated_at),
      badgeLabel: 'refresh',
      sourceLabel: 'Dossier refresh',
      anchorId: 'receipt-dossier-refresh',
      href: '#receipt-dossier-refresh',
      external: false,
      isLatest: false,
    });
  }

  const combined = [...receiptEntries, ...archiveEntries]
    .sort((a, b) => b.occurredAt - a.occurredAt)
    .slice(0, 8)
    .map((item, index) => ({
      ...item,
      isLatest: index === 0,
    }));

  return combined;
}
